import "./reporttable.scss";
import * as React from "react";
import axios, { type AxiosError } from "axios";
import { type PartialRequired } from "../../../../types/internal/utility";
import Cloner from "../../../Utilities/Generic/Cloner";
import { CancellablePromise } from "../../../Utilities/Generic/Promises";
import {
    getFullReport,
    getReport,
    mergeReportData,
    orderReport,
    type RawReportData,
    type RawReportDataLayer,
    type ReportType,
} from "../../../Utilities/Platform/Reports";
import { BodyContext } from "../../Context/BodyContext";
import ReportTableRenderer from "./ReportTableRenderer";

export interface ReportGroup {
    open: boolean;
    children: ReportGroupData;
}

export type ReportGroupData = Record<string, ReportGroup>;

export interface ReportTableResults {
    type: "item" | "group" | "total";
    data: RawReportData;
    parentGroups: Array<string>;
    depth: number;
}

interface Props {
    query: false | string;
    executing?: number;
    onLoaded?: (page: number, err?: AxiosError<{ error: boolean; message: string; }>) => void;
    renderHeader?: (column: string) => React.ReactNode;
    parentElement?: HTMLElement;
    cache?: boolean;
    loadAllData?: boolean;
    nullName?: string;
    showInitialLoading?: boolean;
    layers?: number;
    source?: "microservice" | "monolith";
    topPadding?: number;
    height?: number;
}

interface State {
    columns: Array<string>;
    totals: Record<string, null | string>;
    data: Array<ReportTableResults>;
    openData: Array<ReportTableResults>;
    groups: ReportGroupData;
    page: number;
    maxDepth: number;
    widest: Record<string, string>;
    key: null | string;
    loading: boolean;
    complete: boolean;
    prevReport: null | ReportType;
    initialLoading: boolean;
    nullGroupNames: Array<string>;
}

export default class ReportTable extends React.Component<Props, State> {
    protected cancellablePromise: null | CancellablePromise<ReportType>;
    public static contextType = BodyContext;
    public declare context: React.ContextType<typeof BodyContext>;

    constructor(props: Props) {
        super(props);

        this.state = {
            columns       : [],
            totals        : {},
            data          : [],
            openData      : [],
            groups        : {},
            page          : 0,
            maxDepth      : 0,
            widest        : {},
            key           : null,
            loading       : false,
            complete      : false,
            prevReport    : null,
            initialLoading: true,
            nullGroupNames: [],
        };

        this.cancellablePromise = null;

        this.loadQuery = this.loadQuery.bind(this);
        this.handleGroupClick = this.handleGroupClick.bind(this);
    }

    public componentDidMount(): void {
        if(this.props.query) {
            void this.loadQuery();
        }
    }

    public componentWillUnmount(): void {
        if(this.cancellablePromise) {
            this.cancellablePromise.cancel();
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(
            (
                this.props.query !== prevProps.query ||
                this.props.executing !== prevProps.executing
            ) &&
            this.props.executing !== 0
        ) {
            this.setState({
                page      : 0,
                key       : null,
                loading   : false,
                complete  : false,
                prevReport: null,
            }, () => {
                if(this.cancellablePromise) {
                    this.cancellablePromise.cancel();
                }

                void this.loadQuery(true);
            });
        } else if(prevProps.layers !== this.props.layers) {
            this.setState(prevState => ({
                openData: this.filterData(prevState.data, prevState.groups),
            }));
        }
    }

    protected loadQuery(clear = false): Promise<void> {
        if(!this.props.query || this.state.complete) {
            return Promise.resolve();
        }

        this.setState({
            loading: true,
        });

        // Get current loading page (only used for callbacks)
        const page = this.state.page;

        // Prepare to be able to cancel the request
        const controller = new AbortController();

        // Check if the data should all be loaded
        if(this.props.loadAllData) {
            // Request entire report
            this.cancellablePromise = new CancellablePromise(getFullReport(this.props.query, {
                ignoreDivisionByZero: true,
                sendWidestValues    : true,
                cancelToken         : controller.signal,
                source              : this.props.source,
            }, this.props.cache), () => {
                if(controller) {
                    controller.abort("Promise cancelled");
                }
            });
        } else {
            // Request first page of the report
            this.cancellablePromise = new CancellablePromise(getReport(this.props.query, this.state.key, {
                ignoreDivisionByZero: true,
                sendWidestValues    : true,
                cancelToken         : controller.signal,
                source              : this.props.source,
            }, this.props.cache), () => {
                if(controller) {
                    controller.abort("Promise cancelled");
                }
            });
        }

        return this.cancellablePromise
            .promise
            .then(report => {
                // Prepare the report data for flattening
                let reportData = orderReport(report).data;

                if(this.state.prevReport) {
                    reportData = {
                        ...reportData,
                        data: mergeReportData([
                            this.state.prevReport,
                            {
                                ...report,
                                data: reportData,
                            },
                        ]),
                    };
                }

                const groups = clear ? {} : { ...this.state.groups };
                const newData: Array<ReportTableResults> = [];

                ReportTable.flattenData(reportData.data, groups, newData);

                this.setState((prevState): any => {
                    if(clear) {
                        // prevState.columns  = [];
                        // prevState.totals   = [];
                        // prevState.data     = [];
                        // prevState.openData = [];
                        // prevState.groups   = [];
                    }

                    const newState: Partial<State> = {
                        prevReport: {
                            ...report,
                            data: reportData,
                        },
                        widest        : report.data.widest,
                        page          : prevState.page + 1,
                        key           : report.key,
                        data          : newData,
                        openData      : this.filterData(newData, groups),
                        complete      : report.completed,
                        nullGroupNames: reportData.nullGroupNames,
                        groups,
                    };

                    if(this.state.page === 0) {
                        newState.columns = reportData.columns;
                        newState.totals = reportData.totals;

                        // Calculate max depth
                        newState.maxDepth = 0;

                        for(let i = 0, l = newData.length; i < l; i++) {
                            if(newData[i].depth > newState.maxDepth) {
                                newState.maxDepth = newData[i].depth;
                            }
                        }
                    }

                    return newState;
                });

                if(typeof this.props.onLoaded !== "undefined") {
                    this.props.onLoaded(page);
                }

                this.setState({
                    loading       : false,
                    initialLoading: false,
                });
            })
            .catch(err => {
                if(err.cancelled || axios.isCancel(err)) {
                    return;
                }

                if(err) {
                    this.setState({
                        complete: true,
                    });
                }

                if(typeof this.props.onLoaded !== "undefined") {
                    this.props.onLoaded(page, err);
                }

                this.setState({
                    loading       : false,
                    initialLoading: false,
                });
            });
    }

    public static flattenData(
        data: RawReportDataLayer,
        groups: ReportGroupData,
        results: Array<ReportTableResults>,
        parentGroups: Array<string> = [],
        depth = 0
    ): void {
        if(Array.isArray(data)) {
            // Put the items into the results
            for(let i = 0, l = data.length; i < l; i++) {
                results.push({
                    type: "item",
                    data: data[i],
                    parentGroups,
                    depth,
                });
            }
        } else {
            const groupKeys = Object.keys(data);

            // Loop through groups
            for(let i = 0, l = groupKeys.length; i < l; i++) {
                const group = data[groupKeys[i]];

                const groupName = (group.name ? group.name : "");
                const currentGroup = [
                    ...parentGroups,
                    groupName,
                ];

                // Set the group to open
                if(!groups[groupName]) {
                    groups[groupName] = {
                        open    : true,
                        children: {},
                    };
                }

                // Put the group header into the results
                results.push({
                    type: "group",
                    data: {
                        name: groupName,
                    },
                    parentGroups,
                    depth,
                });

                ReportTable.flattenData(group.data, groups[groupName].children, results, currentGroup, depth + 1);

                // Put group footer into the results
                results.push({
                    type : "total",
                    data : group.totals,
                    parentGroups,
                    depth: depth + 1,
                });
            }
        }
    }

    protected filterData(data: Array<ReportTableResults>, groups: ReportGroupData): Array<ReportTableResults> {
        const openData = [];

        // Loop through the data and remove ones that need to be closed
        for(let i = 0, l = data.length; i < l; i++) {
            const row = data[i];

            if(this.checkGroupOpen(groups, row.parentGroups)) {
                openData.push(row);
            }
        }

        return openData;
    }

    protected checkGroupOpen(group: ReportGroupData, groupArray: Array<string>): boolean {
        const groupDepth = groupArray.length;

        const layers = this.props.layers;

        if(typeof layers !== "number") {
            // There is closed groups
            return true;
        }

        if(groupDepth > layers) {
            return false;
        }

        let groupLayer: ReportGroupData | ReportGroup = group;

        for(let i = 0; i < groupDepth; i++) {
            groupLayer = groupLayer[groupArray[i]];

            if(!groupLayer) {
                break;
            }

            if(!groupLayer.open) {
                return false;
            }

            groupLayer = groupLayer.children;
        }

        return true;
    }

    protected handleGroupClick(groupIndex: Array<string>): void {
        this.setState(prevState => {
            const baseGroups = Cloner.Clone(prevState.groups);

            let groups: PartialRequired<ReportGroup, "children"> = {
                children: baseGroups,
            };

            for(let i = 0, l = groupIndex.length; i < l; i++) {
                groups = groups.children[groupIndex[i]];
            }

            if(groups) {
                groups.open = !groups.open;
            }

            return {
                groups  : baseGroups,
                openData: this.filterData(prevState.data, baseGroups),
            };
        }, () => {
            setTimeout(() => {
                if(this.props.parentElement) {
                    this.props.parentElement.dispatchEvent(new CustomEvent("scroll"));
                } else if(this.context.current) {
                    this.context.current.dispatchEvent(new CustomEvent("scroll"));
                } else {
                    document.body.dispatchEvent(new CustomEvent("scroll"));
                }
            }, 100);
        });
    }

    public render(): React.ReactNode {
        if(this.state.initialLoading && !this.props.showInitialLoading) {
            return <div />;
        }

        return (
            <div className="report-table-wrapper">
                <ReportTableRenderer
                    columns={this.state.columns}
                    renderHeader={this.props.renderHeader}
                    data={this.state.openData}
                    complete={this.state.complete}
                    loading={this.state.loading}
                    groups={this.state.groups}
                    parentElement={this.props.parentElement}
                    nullName={this.props.nullName}
                    totals={this.state.totals}
                    maxDepth={this.state.maxDepth}
                    widest={this.state.widest}
                    layers={this.props.layers}
                    onLoad={this.loadQuery}
                    onGroupClick={this.handleGroupClick}
                    nullGroupNames={this.state.nullGroupNames}
                    topPadding={this.props.topPadding}
                    height={this.props.height}
                />
            </div>
        );
    }
}
